import axios from "src/utils/axios";

const { createSlice } = require("@reduxjs/toolkit");

// 2026-09-22 · Slice del refactor Horarios + Rotaciones separados.
// Coexiste con `employeeSchedule` legacy hasta que la migración se
// complete y la UI vieja pueda deprecarse.

const initialState = {
  // Horarios (biblioteca base)
  workSchedules: [],
  workScheduleById: {},
  isLoadingWorkSchedules: false,
  workScheduleError: null,

  // Rotaciones (biblioteca pública, filtra IsHidden=false)
  rotations: [],
  rotationById: {},
  isLoadingRotations: false,
  rotationError: null,

  // Asignaciones por empleado (cache reducida)
  assignmentsByEmployeeId: {},
  currentAssignmentByEmployeeId: {},

  // 2026-09-28 · Panel V2 en ficha empleado
  // ─ Preview de rotación (rotationPatternId + offset → matriz 7×N)
  rotationPreviewByKey: {},
  isLoadingRotationPreview: false,
  rotationPreviewError: null,

  // ─ Turnos materializados del empleado por rango
  workScheduleItemsByEmployeeId: {},
  isLoadingEmployeeWSI: false,
  employeeWSIError: null,

  // ─ Carga semanal vs contrato
  workloadByEmployeeId: {},
  isLoadingWorkload: false,
  workloadError: null,
};

const slice = createSlice({
  name: "schedulesV2",
  initialState,
  reducers: {
    // ── WorkSchedules ─────────────────────────────────────────
    workSchedulesLoading(state) {
      state.isLoadingWorkSchedules = true;
      state.workScheduleError = null;
    },
    workSchedulesLoaded(state, action) {
      state.isLoadingWorkSchedules = false;
      state.workSchedules = action.payload;
      state.workScheduleById = Object.fromEntries(
        action.payload.map((w) => [w.id, w]),
      );
    },
    workScheduleUpserted(state, action) {
      const w = action.payload;
      const idx = state.workSchedules.findIndex((x) => x.id === w.id);
      if (idx >= 0) state.workSchedules[idx] = w;
      else state.workSchedules.push(w);
      state.workScheduleById[w.id] = w;
    },
    workScheduleDeleted(state, action) {
      const id = action.payload;
      state.workSchedules = state.workSchedules.filter((w) => w.id !== id);
      delete state.workScheduleById[id];
    },
    workScheduleError(state, action) {
      state.isLoadingWorkSchedules = false;
      state.workScheduleError = action.payload;
    },

    // ── Rotaciones ────────────────────────────────────────────
    rotationsLoading(state) {
      state.isLoadingRotations = true;
      state.rotationError = null;
    },
    rotationsLoaded(state, action) {
      state.isLoadingRotations = false;
      state.rotations = action.payload;
      state.rotationById = Object.fromEntries(
        action.payload.map((r) => [r.id, r]),
      );
    },
    rotationUpserted(state, action) {
      const r = action.payload;
      const idx = state.rotations.findIndex((x) => x.id === r.id);
      if (idx >= 0) state.rotations[idx] = r;
      else state.rotations.push(r);
      state.rotationById[r.id] = r;
    },
    rotationDeleted(state, action) {
      const id = action.payload;
      state.rotations = state.rotations.filter((r) => r.id !== id);
      delete state.rotationById[id];
    },
    rotationError(state, action) {
      state.isLoadingRotations = false;
      state.rotationError = action.payload;
    },

    // ── Asignaciones ──────────────────────────────────────────
    assignmentsLoaded(state, action) {
      const { employeeId, assignments } = action.payload;
      state.assignmentsByEmployeeId[employeeId] = assignments;
    },
    currentAssignmentLoaded(state, action) {
      const { employeeId, assignment } = action.payload;
      state.currentAssignmentByEmployeeId[employeeId] = assignment;
    },

    // ── Panel V2 · Preview / Cuadrante / Workload ─────────────
    rotationPreviewLoading(state) {
      state.isLoadingRotationPreview = true;
      state.rotationPreviewError = null;
    },
    rotationPreviewLoaded(state, action) {
      state.isLoadingRotationPreview = false;
      const { key, preview } = action.payload;
      state.rotationPreviewByKey[key] = preview;
    },
    rotationPreviewError(state, action) {
      state.isLoadingRotationPreview = false;
      state.rotationPreviewError = action.payload;
    },
    employeeWSILoading(state) {
      state.isLoadingEmployeeWSI = true;
      state.employeeWSIError = null;
    },
    employeeWSILoaded(state, action) {
      state.isLoadingEmployeeWSI = false;
      const { employeeId, items } = action.payload;
      state.workScheduleItemsByEmployeeId[employeeId] = items;
    },
    employeeWSIError(state, action) {
      state.isLoadingEmployeeWSI = false;
      state.employeeWSIError = action.payload;
    },
    workloadLoading(state) {
      state.isLoadingWorkload = true;
      state.workloadError = null;
    },
    workloadLoaded(state, action) {
      state.isLoadingWorkload = false;
      const { employeeId, workload } = action.payload;
      state.workloadByEmployeeId[employeeId] = workload;
    },
    workloadError(state, action) {
      state.isLoadingWorkload = false;
      state.workloadError = action.payload;
    },
  },
});

export default slice.reducer;

const {
  workSchedulesLoading,
  workSchedulesLoaded,
  workScheduleUpserted,
  workScheduleDeleted,
  workScheduleError,
  rotationsLoading,
  rotationsLoaded,
  rotationUpserted,
  rotationDeleted,
  rotationError,
  assignmentsLoaded,
  currentAssignmentLoaded,
  rotationPreviewLoading,
  rotationPreviewLoaded,
  rotationPreviewError,
  employeeWSILoading,
  employeeWSILoaded,
  employeeWSIError,
  workloadLoading,
  workloadLoaded,
  workloadError,
} = slice.actions;

// ────────────────── Thunks Horarios ──────────────────

export function fetchWorkSchedules(includeInactive = false) {
  return async (dispatch) => {
    dispatch(workSchedulesLoading());
    try {
      const res = await axios.get(
        `workSchedule?includeInactive=${includeInactive}`,
      );
      dispatch(workSchedulesLoaded(res.data));
    } catch (e) {
      dispatch(workScheduleError(e?.message || "error"));
    }
  };
}

export function upsertWorkSchedule(dto) {
  return async (dispatch) => {
    const res = await axios.post("workSchedule", dto);
    dispatch(workScheduleUpserted(res.data));
    return res.data;
  };
}

/**
 * 2026-09-23 · Devuelve detalle de uso — cuántas rotaciones,
 * asignaciones activas, WSI futuros afectados por un edit.
 */
export async function getWorkScheduleUsageDetail(id) {
  const res = await axios.get(`workSchedule/${id}/usage-detail`);
  return res.data;
}

/**
 * 2026-09-23 · Clona un horario base. Devuelve el nuevo horario
 * ({id, name: "<original> (Copia)", ...}). El caller normalmente
 * abre el editor sobre el clon acto seguido.
 */
export function cloneWorkSchedule(id) {
  return async (dispatch) => {
    const res = await axios.post(`workSchedule/${id}/clone`);
    dispatch(workScheduleUpserted(res.data));
    return res.data;
  };
}

export function deleteWorkSchedule(id) {
  return async (dispatch) => {
    await axios.delete(`workSchedule/${id}`);
    dispatch(workScheduleDeleted(id));
  };
}

export async function getWorkScheduleUsage(id) {
  const res = await axios.get(`workSchedule/${id}/usage`);
  return res.data.count;
}

// ────────────────── Thunks Rotaciones ──────────────────

export function fetchRotations(includeInactive = false) {
  return async (dispatch) => {
    dispatch(rotationsLoading());
    try {
      const res = await axios.get(
        `rotationV2/library?includeInactive=${includeInactive}`,
      );
      dispatch(rotationsLoaded(res.data));
    } catch (e) {
      dispatch(rotationError(e?.message || "error"));
    }
  };
}

export function fetchRotationById(id) {
  return async (dispatch) => {
    const res = await axios.get(`rotationV2/${id}`);
    dispatch(rotationUpserted(res.data));
    return res.data;
  };
}

export function upsertRotation(dto) {
  return async (dispatch) => {
    const res = await axios.post("rotationV2", dto);
    dispatch(rotationUpserted(res.data));
    return res.data;
  };
}

export function deleteRotation(id) {
  return async (dispatch) => {
    await axios.delete(`rotationV2/${id}`);
    dispatch(rotationDeleted(id));
  };
}

// ────────────────── Thunks Asignaciones ──────────────────

export function fetchEmployeeAssignments(employeeId) {
  return async (dispatch) => {
    const res = await axios.get(`rotationV2/assign/employee/${employeeId}`);
    dispatch(assignmentsLoaded({ employeeId, assignments: res.data }));
  };
}

export function fetchEmployeeCurrentAssignment(employeeId) {
  return async (dispatch) => {
    try {
      const res = await axios.get(
        `rotationV2/assign/employee/${employeeId}/current`,
      );
      dispatch(
        currentAssignmentLoaded({ employeeId, assignment: res.data }),
      );
      return res.data;
    } catch {
      dispatch(currentAssignmentLoaded({ employeeId, assignment: null }));
      return null;
    }
  };
}

export function assignFixedSchedule(body) {
  return async (dispatch) => {
    const res = await axios.post("rotationV2/assign/fixed", body);
    dispatch(
      currentAssignmentLoaded({
        employeeId: body.employeeId,
        assignment: res.data,
      }),
    );
    return res.data;
  };
}

export function assignRotation(body) {
  return async (dispatch) => {
    const res = await axios.post("rotationV2/assign/rotation", body);
    dispatch(
      currentAssignmentLoaded({
        employeeId: body.employeeId,
        assignment: res.data,
      }),
    );
    return res.data;
  };
}

export function terminateAssignment(assignId, validTo) {
  return async () => {
    const q = validTo ? `?validTo=${validTo}` : "";
    await axios.post(`rotationV2/assign/${assignId}/terminate${q}`);
  };
}

/**
 * 2026-09-28 · Borrado destructivo de una asignación completa. Marca
 * IsActive=false + ValidTo=hoy y elimina TODOS los WSI asociados
 * (pasados incluidos), respetando fichajes reales y turnos en curso.
 * Devuelve `{ workScheduleItemsDeleted, workScheduleItemsPreserved,
 * assignmentsClosed, fromDate }`.
 */
export function deleteAssignment(assignId) {
  return async () => {
    const res = await axios.delete(`rotationV2/assign/${assignId}`);
    return res.data;
  };
}

/**
 * 2026-09-28 · Elimina la planificación FUTURA del empleado desde
 * `fromDate` (default: hoy). Cierra assignments V2 vigentes/futuras
 * y borra WSI cuyo Start >= fromDate, respetando:
 *   - Turnos con fichajes reales (nunca se borran).
 *   - Turno en curso (ya empezó, aún no ha acabado).
 * Endpoint stand-alone para el botón "Eliminar horario" en Cuadrantes.
 * Devuelve `{ workScheduleItemsDeleted, workScheduleItemsPreserved,
 * assignmentsClosed, fromDate }`.
 */
export function deleteFutureSchedule(employeeId, fromDate = null) {
  return async (dispatch) => {
    const q = fromDate ? `?fromDate=${fromDate}` : "";
    const res = await axios.delete(
      `rotationV2/employee/${employeeId}/future${q}`,
    );
    // Al vaciar futuros ya no hay assignment vigente — reset del
    // slice para que el dialog abra sin pre-load de la anterior.
    dispatch(
      currentAssignmentLoaded({
        employeeId,
        assignment: null,
      }),
    );
    return res.data;
  };
}

/**
 * Regenera los WorkScheduleItems del empleado en el horizonte
 * [from, to] (default: hoy → +60 días). Respeta WSI con fichaje real
 * o override. Útil tras reasignar horario o para forzar recálculo.
 * Devuelve `{generated, from, to}`.
 */
export function materializeEmployeeSchedule(employeeId, opts = {}) {
  return async () => {
    const params = new URLSearchParams();
    if (opts.from) params.set("from", opts.from);
    if (opts.to) params.set("to", opts.to);
    const q = params.toString() ? `?${params}` : "";
    const res = await axios.post(
      `rotationV2/materialize/${employeeId}${q}`,
    );
    return res.data;
  };
}

// ────────────────── Thunks Panel V2 ──────────────────

/**
 * 2026-09-28 · Preview de una rotación con un offset dado. Devuelve el
 * ciclo completo semana a semana con nombre/color del horario en cada
 * celda — se renderiza como matriz 7×N en el diálogo de asignación
 * ANTES de aplicar. No persiste nada.
 */
export function fetchRotationPreview(rotationPatternId, startWeekOffset = 0) {
  return async (dispatch) => {
    const key = `${rotationPatternId}:${startWeekOffset}`;
    dispatch(rotationPreviewLoading());
    try {
      const res = await axios.get(
        `rotationV2/preview?rotationPatternId=${rotationPatternId}&startWeekOffset=${startWeekOffset}`,
      );
      dispatch(rotationPreviewLoaded({ key, preview: res.data }));
      return res.data;
    } catch (e) {
      dispatch(rotationPreviewError(e?.message || "error"));
      throw e;
    }
  };
}

/**
 * 2026-09-28 · Turnos materializados del empleado en [from, to].
 * Alimenta el cuadrante semanal del panel V2. `from`/`to` en formato
 * ISO date (YYYY-MM-DD).
 */
export function fetchEmployeeWorkScheduleItems(employeeId, from, to) {
  return async (dispatch) => {
    dispatch(employeeWSILoading());
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const q = params.toString() ? `?${params}` : "";
      const res = await axios.get(
        `rotationV2/employee/${employeeId}/workScheduleItems${q}`,
      );
      dispatch(employeeWSILoaded({ employeeId, items: res.data }));
      return res.data;
    } catch (e) {
      dispatch(employeeWSIError(e?.message || "error"));
      throw e;
    }
  };
}

/**
 * 2026-09-28 · Carga semanal del empleado (horas planificadas por ISO
 * week vs máximo del contrato). Alimenta el widget "Contrato" del panel
 * V2 con status OK / WARN / OVER por semana.
 */
export function fetchEmployeeWorkload(employeeId, from, to) {
  return async (dispatch) => {
    dispatch(workloadLoading());
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const q = params.toString() ? `?${params}` : "";
      const res = await axios.get(
        `rotationV2/employee/${employeeId}/workload${q}`,
      );
      dispatch(workloadLoaded({ employeeId, workload: res.data }));
      return res.data;
    } catch (e) {
      dispatch(workloadError(e?.message || "error"));
      throw e;
    }
  };
}
